// src/features/rating/ui/RatingModal.jsx
/**
 *
 *  Class Rating Modal
 *  Component
 *
 *  Created by Ron on 2025-09-04.
 *  Copyright (c) 2025-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import moment from "moment-timezone";

import { useAppState } from "../../state";
import { useRegion } from "../../context/RegionProvider";

// Components
import Button from "../Button/Button";
import { Skeleton } from "../Skeleton/Skeleton";
import { LikeButton } from "./LikeButton/LikeButton"; // твой новый компонент

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";
import {
  useGetUnratedEvents,
  useRateEvent,
} from "../../hooks/useUnratedEvents/useUnratedEvents";

// Utils
import { clientWidth } from "../../utils";
import { modalStyles } from "../../constants";
import { getTz } from "../../utils/date";

// Icons
import { Heart } from "./LikeButton/Heart";
import ArrowRightIcon from "../../icons/ArrowRightIcon";

export function EventRatingModal({ onClose }) {
  const { theme } = useTheme();
  const { region } = useRegion();
  const { user, hasTriedToAutoLogin } = useAppState();

  const customStyle =
    clientWidth() < theme.responsive.breakpoints.sm
      ? Object.assign(
          {},
          { ...modalStyles.newMobileDefault },
          {
            content: {
              ...modalStyles.newMobileDefault.content,
              padding: "48px 24px 24px",
              top: "auto",
              bottom: "0px",
            },
          }
        )
      : Object.assign(
          {},
          { ...modalStyles.default },
          {
            content: {
              ...modalStyles.default.content,
              width: "600px",
              padding: "80px 24px 24px",
            },
          }
        );

  const { data: events, isLoading } = useGetUnratedEvents(user && user.id);
  const rateEvent = useRateEvent();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [mode, setMode] = useState("like");
  const [liked, setLiked] = useState(false);
  const [report, setReport] = useState("");
  const [isRated, setIsRated] = useState(false);
  const [dismissedEvents, setDismissedEvents] = useState(new Set());
  const [manuallyClosed, setManuallyClosed] = useState(false);
  const [animateLike, setAnimateLike] = useState(false);

  // Используем ваши тестовые данные
  const unratedEvents =
    events &&
    events.data &&
    events.data.content &&
    events.data.content.length > 0
      ? events.data.content
      : [];

  const currentEvent = unratedEvents[currentEventIndex];

  // Автооткрытие модалки
  useEffect(() => {
    if (
      manuallyClosed ||
      isModalOpen ||
      !hasTriedToAutoLogin ||
      !user?.id ||
      unratedEvents?.length === 0
    ) {
      return;
    }

    const nextEventIndex = unratedEvents.findIndex(
      (ev) => !dismissedEvents.has(ev.id)
    );

    if (nextEventIndex !== -1) {
      setCurrentEventIndex(nextEventIndex);
      setIsModalOpen(true);
    }
  }, [
    manuallyClosed,
    isModalOpen,
    hasTriedToAutoLogin,
    user?.id,
    unratedEvents,
    dismissedEvents,
  ]);

  // Централизованный сброс состояния при смене события
  useEffect(() => {
    if (currentEvent?.id) {
      setMode("like");
      setLiked(false);
      setReport("");
      setIsRated(false);
    }
  }, [currentEvent?.id]);

  // Проверка индекса
  useEffect(() => {
    if (
      isModalOpen &&
      unratedEvents.length > 0 &&
      currentEventIndex >= unratedEvents.length
    ) {
      setCurrentEventIndex(0);
    }
  }, [isModalOpen, unratedEvents, currentEventIndex]);

  const handleLike = async () => {
    if (!currentEvent) return;

    try {
      await rateEvent.mutateAsync({
        params: {
          path: { id: currentEvent.id },
          query: {
            customerId: user?.id,
            rating: "LIKE",
          },
        },
      });
    } catch (error) {
      console.error("Failed to rate event:", error);
    } finally {
      handleClose(true); // закрываем модалку, помечаем ивент как обработанный
    }
  };

  const handleComment = () => {
    // При переходе к комментарию снимаем лайк
    if (liked) {
      setLiked(false);
      setIsRated(false);
    }
    //
    // setMode("report");
    // setLiked(false); // сбрасываем состояние лайка при переходе
    setMode("report");
  };

  const handleSubmitReport = async () => {
    if (!currentEvent) return;

    // Если был лайк, проигрываем анимацию перед отправкой
    if (liked) {
      setAnimateLike(true); // запускаем анимацию

      // Ждем завершения анимации
      await new Promise((resolve) => setTimeout(resolve, 800));

      setAnimateLike(false); // сбрасываем
    }

    // Отправляем данные
    try {
      await rateEvent.mutateAsync({
        params: {
          path: { id: currentEvent.id },
          query: {
            customerId: user?.id,
            rating: liked ? "LIKE" : "DISLIKE",
            comment: report || undefined,
          },
        },
      });
    } catch (error) {
      console.error("Failed to submit comment:", error);
    } finally {
      handleClose(true);
    }
  };

  const handleClose = (completed = false) => {
    if (!completed) {
      setManuallyClosed(true);
    }

    if (currentEvent) {
      setDismissedEvents((prev) => new Set(prev).add(currentEvent.id));
    }

    // setMode("like");
    // setLiked(false);
    // setReport("");
    // setIsRated(false);

    const nextIndex = unratedEvents.findIndex(
      (ev, i) => i > currentEventIndex && !dismissedEvents.has(ev.id)
    );

    if (nextIndex !== -1) {
      setCurrentEventIndex(nextIndex);
    } else {
      setIsModalOpen(false);
      if (onClose) onClose();
    }
  };

  if (isLoading) {
    return (
      <Modal
        isOpen={isModalOpen && isLoading}
        onRequestClose={handleClose}
        style={customStyle}
        contentLabel="Loading"
        className="tbk-fixed tbk-flex tbk-flex-col tbk-items-center tbk-rounded-2xl tbk-bg-basic-white tbk-p-6 tbk-pt-8 tbk-shadow-trube lg:tbk-relative lg:tbk-w-[320px]"
      >
        <div className="tbk-absolute tbk--top-5">
          <img className="tbk-h-9 tbk-w-9 tbk-rounded-full tbk-border-2 tbk-border-basic-white tbk-bg-blue-grey-light lg:tbk-h-28 lg:tbk-w-28" />
        </div>
        <div className="tbk-mt-10 tbk-text-center">
          <Skeleton className="tbk-mb-1_ tbk-inline-block tbk-h-[36px] tbk-w-85 tbk-bg-blue-grey-light" />
          <Skeleton className="tbk-mb-2_ tbk-inline-block tbk-h-[26px] tbk-w-88 tbk-bg-blue-grey-light" />
        </div>
        <button
          className={`tbk-mb-2_ tbk-group tbk-mt-2 tbk-flex tbk-flex-col tbk-items-center`}
        >
          <div
            className={`duration-200 tbk-group-hover:tbk-border-coral tbk-group-hover:tbk-bg-coral-light/10 tbk-rounded-full tbk-border-2 tbk-border-blue-grey-light tbk-p-2.5 tbk-transition-all`}
          >
            <Heart
              className={`duration-200 tbk-group-hover:tbk-text-coral tbk-h-5 tbk-w-5 tbk-text-blue-grey tbk-transition-all`}
            />
          </div>
          <span className={`tbk-text-small tbk-mt-1 tbk-text-blue-grey`}>
            Loved it
          </span>
        </button>
        <div className="tbk-text-main tbk-mt-5 tbk-text-primary">
          Loading...
        </div>
      </Modal>
    );
  }

  if (!currentEvent) {
    return null; // или просто ничего не показывай, пока нет событий
  }

  const eventStart =
    currentEvent && moment.utc(currentEvent.start).tz(getTz(region));

  return (
    <Modal
      isOpen={isModalOpen}
      onRequestClose={handleClose}
      style={customStyle}
      contentLabel="Rate Event"
      className="tbk-fixed tbk-flex tbk-flex-col tbk-items-center tbk-rounded-2xl tbk-bg-basic-white tbk-p-6 tbk-pt-8 tbk-shadow-trube tbk-outline-none lg:tbk-relative lg:tbk-w-[320px]"
      closeTimeoutMS={300}
    >
      {/* Close button */}
      <button
        onClick={handleClose}
        className="tbk-absolute tbk-right-2 tbk-top-2 tbk-text-coral hover:tbk-text-coral-light"
      >
        ✕
      </button>

      {/* Avatar */}
      <div className="tbk-absolute tbk--top-5">
        <img
          src={currentEvent.pastPhoto}
          // alt="class"
          alt={currentEvent.name}
          className="tbk-h-9 tbk-w-9 tbk-rounded-full tbk-border-2 tbk-border-basic-white tbk-bg-blue-grey-light lg:tbk-h-28 lg:tbk-w-28"
        />
      </div>

      <div className="tbk-mt-10 tbk-text-center">
        {mode === "like" ? (
          <>
            <h2 className="tbk-text-title-bold tbk-text-primary">
              Enjoy the Class?
            </h2>
            <p className="tbk-text-main tbk-mt-1 tbk-text-primary">
              {currentEvent.name}, {eventStart.format("MMM D")}
            </p>
          </>
        ) : (
          <>
            <h2 className="tbk-text-title-bold tbk-text-primary">
              Share your thoughts
            </h2>
            <p className="tbk-text-main tbk-mt-1 tbk-text-primary">
              Loved it? Had a breakthrough? Drop a quick comment and let us
              know!
            </p>
          </>
        )}
      </div>

      {/* LikeButton с анимацией */}

      <LikeButton
        key={`${mode}-${currentEvent?.id}`}
        className="tbk-mt-2"
        animate={mode === "like"} // в режиме like - с анимацией, в report - без
        liked={mode === "like" ? undefined : liked} // controlled state только в режиме report
        triggerAnimation={animateLike} // внешний триггер анимации
        onRated={async (newState) => {
          if (mode === "like") {
            await handleLike();
          } else {
            if (newState !== undefined) {
              setLiked(newState);
            }
          }
        }}
      />

      {mode === "like" ? (
        <>
          {/* Comment button */}
          <Button
            variant="secondary"
            onClick={handleComment}
            className="tbk-mt-5 tbk-w-full"
          >
            Comment
          </Button>
        </>
      ) : (
        <>
          {/* Report textarea */}
          <div className="tbk-mt-2 tbk-w-full">
            <div className="tbk-text-small tbk-mb-0.5 tbk-text-primary">
              Your report:
            </div>
            <textarea
              className="tbk-mt-6_ tbk-text-main focus:tbk-ring-blue-400 focus:tbk-border-blue-400 tbk-w-full tbk-rounded-lg tbk-border tbk-border-blue-grey-light tbk-p-2 tbk-text-blue-grey focus:tbk-ring-2"
              placeholder="Share your experience..."
              value={report}
              onChange={(e) => setReport(e.target.value)}
              rows={3}
            />
          </div>

          {/* Submit with comment */}
          <Button
            variant="secondary"
            onClick={handleSubmitReport}
            // disabled={!report.trim()}
            className="tbk-bg-primarye hover:tbk-bg-primarye/90 tbk-mt-4 tbk-w-full tbk-rounded-lg tbk-py-2 tbk-font-medium tbk-text-basic-white tbk-transition-colors disabled:tbk-cursor-not-allowed disabled:tbk-opacity-50"
            endIcon={<ArrowRightIcon className="tbk-ml-0.5" />}
          >
            Submit
          </Button>
        </>
      )}
    </Modal>
  );
}
